import { useEffect, useState, useCallback } from 'react'
import { CreateUserForm } from '@/components/users/create-user-form'
import { UserTable } from '@/components/users/user-table'
import { listUsers } from '@/api/users'
import { UserInfo } from '@/lib/types'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { RefreshCw } from 'lucide-react'

export function UsersPage() {
  const [users, setUsers] = useState<UserInfo[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchUsers = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    const result = await listUsers()
    setIsLoading(false)

    if (result.success && result.data) {
      setUsers(result.data.users)
    } else {
      setError(result.error || 'Failed to load users')
    }
  }, [])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">User Management</h1>
        <p className="text-muted-foreground">Create and manage system users</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <CreateUserForm onSuccess={fetchUsers} />

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Users</CardTitle>
                <CardDescription>
                  {users.length} user{users.length !== 1 ? 's' : ''} in the system
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="icon"
                onClick={fetchUsers}
                disabled={isLoading}
              >
                <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {error ? (
              <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive">
                {error}
              </div>
            ) : isLoading && users.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <p className="text-muted-foreground">Loading users...</p>
              </div>
            ) : (
              <UserTable users={users} onRefresh={fetchUsers} />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
